// One score: 100 minus what is still open. Higher is safer.
// Points per open finding by severity; "likely" and "in progress" take about half; each category caps at 40.
import type { Finding, Severity, Category } from './types';

export const POINTS: Record<Severity, { full: number; reduced: number }> = {
  critical: { full: 15, reduced: 7 },
  high:     { full: 8,  reduced: 4 },
  medium:   { full: 3,  reduced: 1 },
  low:      { full: 1,  reduced: 0 },
};
export const CATEGORY_CAP = 40;

export type Band = 'protected' | 'fair' | 'at_risk' | 'high_risk';
export const band = (v: number): Band => (v >= 80 ? 'protected' : v >= 60 ? 'fair' : v >= 40 ? 'at_risk' : 'high_risk');
export const BAND_LABEL: Record<Band, string> = { protected: 'Protected', fair: 'Fair', at_risk: 'At risk', high_risk: 'High risk' };

export function pointsOff(f: Finding): number {
  if (f.status === 'resolved' || f.status === 'dismissed') return 0;
  const p = POINTS[f.severity];
  return f.confidence === 'likely' || f.status === 'in_progress' ? p.reduced : p.full;
}

export interface Score {
  value: number;
  band: Band;
  breakdown: Record<Category, number>;        // points lost per category (after cap)
  drivers: { findingId: string; points: number }[]; // top 5 by impact
}

export function computeScore(findings: Finding[]): Score {
  const raw: Record<Category, number> = { dark_web: 0, money: 0, public_profiles: 0, location: 0, data_brokers: 0 };
  const drivers: { findingId: string; points: number }[] = [];
  for (const f of findings) {
    const p = pointsOff(f);
    if (!p) continue;
    raw[f.category] += p;
    drivers.push({ findingId: f.id, points: p });
  }
  const breakdown = Object.fromEntries(
    Object.entries(raw).map(([c, v]) => [c, Math.min(v, CATEGORY_CAP)]),
  ) as Record<Category, number>;
  const lost = Object.values(breakdown).reduce((a, b) => a + b, 0);
  const value = Math.max(0, 100 - lost);
  drivers.sort((a, b) => b.points - a.points);
  return { value, band: band(value), breakdown, drivers: drivers.slice(0, 5) };
}

// Points a fix would return if completed: used to order "Your plan this week".
export const impactOf = (f: Finding) => pointsOff(f);
